import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage, injectIntl } from "react-intl";

import { Heading3, Paragraph, Icon, Button, Loading } from "../../components";
import { deviceLocation } from "../../utils/geolocation";
import Address from "./Address";
import searchImage from "../../assets/search-icon.png";

/* Everything on the map hangs off a position, so without one there is nothing to show and
   nothing to search. Rather than an empty map, ask for the position: a place to search in, or
   the device's own location. */

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 15;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 9rem 1rem 1rem;
  overflow-y: auto;
  pointer-events: none;

  @media (${props => props.theme.tabletScreen}) {
    align-items: center;
    padding: 11rem 1rem 1rem;
  }
`;

const Card = styled.section`
  width: 100%;
  max-width: 26rem;
  padding: 1.5rem;
  border-radius: ${props => props.theme.borderRadiusLarge};
  background-color: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.25);
  text-align: center;
  pointer-events: all;

  ${Heading3} {
    font-size: 1.375rem;
  }

  ${Paragraph} {
    color: ${props => props.theme.dark};
    font-size: ${props => props.theme.fontSizes.small};
  }
`;

const SearchIcon = styled.img`
  width: 100%;
  height: auto;
  max-width: 88px;
  display: inline-block;
  margin: 0 auto 0.5rem;
`;

/* The address field carries its own label through the placeholder, so the fieldset only needs
   to separate the two ways of answering. Its FloatingContainer caps itself at the width the
   floating search bar wants; in this card it fills the row, like the button below it. */
const Field = styled.div`
  margin: 1rem 0 0.75rem;
  text-align: left;

  & > div {
    max-width: 100%;
  }
`;

const Or = styled.p`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.75rem 0;
  color: ${props => props.theme.gray500};
  font-size: ${props => props.theme.fontSizes.tiny};
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: ${props => props.theme.gray300};
  }
`;

const DeviceButton = styled(Button)`
  width: 100%;
  justify-content: center;
`;

const Failed = styled.p`
  margin-top: 0.75rem;
  color: ${props => props.theme.danger};
  font-size: ${props => props.theme.fontSizes.small};
`;

const LocationPrompt = ({ address, onSelect, onClear, intl }) => {
  const [locating, setLocating] = useState(false);
  const [failed, setFailed] = useState(false);

  const useDeviceLocation = () => {
    setLocating(true);
    setFailed(false);

    deviceLocation()
      .then(position => {
        setLocating(false);
        onSelect(intl.formatMessage({ id: "components.Address.currentPosition" }), position);
      })
      .catch(error => {
        setLocating(false);
        setFailed(true);
        console.log("Couldn't get user's location", error);
      });
  };

  return (
    <Wrapper>
      <Card>
        <SearchIcon src={searchImage} alt="" />
        <Heading3>
          <FormattedMessage id="pages.SearchMap.location.title" />
        </Heading3>
        <Paragraph>
          <FormattedMessage id="pages.SearchMap.location.intro" />
        </Paragraph>

        <Field>
          {/* Asking for the device location behind the visitor's back would race the button
              below, so here the field only searches places. */}
          <Address
            onSelect={(selected, { lat, lng }) => onSelect(selected, { lat, lng })}
            onClear={onClear}
            address={address}
            autoLocate={false}
            autoFocus={false}
            defaultExpanded
          />
        </Field>

        <Or>
          <FormattedMessage id="pages.SearchMap.location.or" />
        </Or>

        <DeviceButton color="info" onClick={useDeviceLocation} disabled={locating}>
          {locating ? (
            <Loading small />
          ) : (
            <>
              <Icon name="location-dot" size="small" color="white" />
              <FormattedMessage id="pages.SearchMap.location.useDevice" />
            </>
          )}
        </DeviceButton>

        {failed && (
          <Failed>
            <FormattedMessage id="pages.SearchMap.location.failed" />
          </Failed>
        )}
      </Card>
    </Wrapper>
  );
};

LocationPrompt.propTypes = {
  address: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

LocationPrompt.defaultProps = {
  address: null,
};

export default injectIntl(LocationPrompt);
